import { Component, Injector, OnInit, Input, ElementRef, Output, EventEmitter } from '@angular/core';
import { BaseFormComponent } from '../base-form.component';
import { ApplicationService } from '../../../../services/application/application.service';
import _ from 'lodash';

@Component({
  moduleId: module.id,
  selector: 'text-autocomplete',
  host: {
    '(document:click)': 'handleClick($event)',
  },
  templateUrl: './text-autocomplete.component.html',
})
export class TextAutoCompleteComponent extends BaseFormComponent implements OnInit {
  @Input() propertyName: string;
  @Input() placeholder: string;
  @Input() disableConditionally: boolean = false;
  @Input() useValuesFromProperty: boolean = false;
  @Output() valueChanged: EventEmitter<string> = new EventEmitter<string>();
  protected preSelectedValue: boolean;
  options: Array<any>;
  query: any = { listValue: '', fieldValue: '' };
  filteredList: Array<{ listValue: string; fieldValue: string }> = [];
  elementRef: ElementRef;
  errorMessage: any = {};
  public selectedIndex: number = 0;
  componentId: string = '';
  allowNew = ['ftMexicoSpecific'];
  isSelect: boolean = false;

  constructor(protected injector: Injector, protected appService: ApplicationService, myElement: ElementRef) {
    super(injector);
    this.elementRef = myElement;
  }

  protected incrementSelectedIndex() {
    this.selectedIndex =
      this.selectedIndex < this.filteredList.length - 1 ? this.selectedIndex + 1 : this.selectedIndex;
  }

  protected decrementSelectedIndex() {
    this.selectedIndex = this.selectedIndex > 0 ? this.selectedIndex - 1 : this.selectedIndex;
  }

  /*
        first touch router of key events.
     */
  async handleKeyUpInteractions(keyCode) {
    if (keyCode === 13 && this.filteredList.length) {
      // enter
      await this.select(this.filteredList[this.selectedIndex]);
    } else if (keyCode === 38) {
      // up
      this.decrementSelectedIndex();
    } else if (keyCode === 40) {
      // down
      this.incrementSelectedIndex();
    } else {
      // any other key, the user is typing: reset the index and filter
      this.resetSelectedIndex();
      this.filter();
    }
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.propertyName) {
      this.propertyName = this.handleIterate(this.propertyName);
      this.prop = this.propertyName;
    } else {
      this.propertyName = this.prop;
    }
    this.componentId = this.questionItem.id + '_' + this.propertyName;
    this.updateFromModel();
  }

  protected setOptions() {
    const valuesForProp = this.getValues();
    /**
     * valuesForProp needs to be massaged into an Array of objects
     * that contain 'listValue' and 'fieldValue' for TextAutoCompleteComponent to work
     */
    if (Array.isArray(valuesForProp) && valuesForProp.length && valuesForProp[0].fieldValue) {
      this.options = valuesForProp;
    } else if (Array.isArray(valuesForProp)) {
      this.options = valuesForProp.map((value) => {
        return { listValue: value, fieldValue: value };
      });
    } else {
      this.options = _.map(Object.keys(valuesForProp), (elem) => {
        return {
          listValue: valuesForProp[elem],
          fieldValue: elem,
        };
      });
    }
    this.options = this.options.sort();
  }

  updateFromModel() {
    this.setOptions();
    const value = this.getValue(this.propertyName) ? this.getValue(this.propertyName) + '' : '';
    const prop = this.propertyName.substr(this.propertyName.lastIndexOf('.') + 1);
    if (value !== this.query.fieldValue) {
      const currQuery = this.query;
      this.query = Object.assign(
        {},
        this.options.find((entry) => entry.fieldValue === value)
      );
      if (!Object.keys(this.query).length) {
        this.query = currQuery;
      }
      if (value && this.allowNew.includes(prop)) {
        this.query.listValue = value;
      }
    }
  }

  resetValue() {
    this.query = { listValue: '', fieldValue: '' };
  }

  async trigger() {
    const prop = this.propertyName.substr(this.propertyName.lastIndexOf('.') + 1);
    const allowNew: boolean = this.allowNew.includes(prop);
    if (!this.query.fieldValue || allowNew) {
      this.query.fieldValue = this.query.listValue;
    }

    const matchedOption = this.options.find(
      (entry) => entry.listValue.toLowerCase() === this.query.listValue.toLowerCase()
    );
    let valueToSave = matchedOption && matchedOption.fieldValue;
    if (!valueToSave) {
      if (allowNew) {
        valueToSave = this.query.fieldValue;
      } else {
        this.resetValue();
      }
    }
    await this.validateAndSet(this.propertyName, valueToSave || '');
    this.valueChanged.emit(valueToSave || '');
  }

  filter() {
    let searchTerm = this.query.listValue;
    if (searchTerm !== '') {
      searchTerm = searchTerm.toLowerCase();
      this.filteredList = this.options.filter(function (el) {
        return el.listValue.toLowerCase().substring(0, searchTerm.length) === searchTerm;
      });
    } else {
      this.filteredList = [];
    }
  }

  handleBlur() {
    if (!this.errorMessage[this.propertyName]) {
      this.checkEmptyAndValidate(this.propertyName, this.query.fieldValue);
    }
  }

  async select(item) {
    this.query = Object.assign({}, item);
    this.filteredList = [];
    this.resetSelectedIndex();
    await this.trigger();
  }

  protected resetSelectedIndex() {
    this.selectedIndex = 0;
  }

  handleClick(event) {
    let clickedComponent = event.target;
    let inside = false;

    do {
      if (clickedComponent === this.elementRef.nativeElement) {
        inside = true;
      }
      clickedComponent = clickedComponent.parentNode;
    } while (clickedComponent);

    if (!inside) {
      this.filteredList = [];
    }
  }

  isError(): boolean {
    return !!this.errorMessage[this.propertyName];
  }

  getErrorMessage(): string {
    return this.errorMessage[this.propertyName];
  }

  getClasses() {
    const error = this.isError();
    const isComplete = !error && !!this.query;
    return {
      'complete': isComplete,
      'error': error,
      'select__input': this.isSelect,
      'select__input--padding-right': this.isSelect,
    };
  }

  protected parse(prop: string, value): string {
    let parsedValue = value;
    const typedValue = value.toLowerCase();

    const matchingOption = _.find(this.options, function (option) {
      return typedValue === option.listValue.toLowerCase() || typedValue === option.fieldValue.toLowerCase();
    });

    if (matchingOption) {
      parsedValue = matchingOption.fieldValue;
    } else if (this.preSelectedValue) {
      parsedValue = '';
    }

    return parsedValue;
  }

  handleTab() {
    this.filteredList = [];
  }

  getValues() {
    return this.useValuesFromProperty
      ? this.appSvc.getValuesFor(this.propertyName)
      : this.getDescriptionsFor(this.propertyName) || this.appSvc.getValuesFor(this.propertyName);
  }
}
